// Dark leads; light is complete. The choice is a per-browser convenience: index.html
// applies it before the first paint, this module keeps it in step afterwards.
import { ref } from "vue";
import { applyGraphTheme } from "@/lib/graph/theme";

export type Theme = "dark" | "light";
const KEY = "rushd.theme";

function stored(): Theme | null {
  try {
    const v = localStorage.getItem(KEY);
    return v === "light" || v === "dark" ? v : null;
  } catch {
    return null; // storage blocked (private mode, previews): fall back to dark
  }
}

export function initialTheme(): Theme {
  return stored() ?? "dark";
}

const theme = ref<Theme>(initialTheme());

export function applyTheme(t: Theme): void {
  applyGraphTheme(t); // before the ref changes: the map redraws on it
  theme.value = t;
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("data-theme", t);
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", t === "dark" ? "#10141b" : "#f3f5f6");
  }
  try { localStorage.setItem(KEY, t); } catch { /* the page still renders without it */ }
}

export function useTheme() {
  return {
    theme,
    toggle: () => applyTheme(theme.value === "dark" ? "light" : "dark"),
    set: applyTheme,
  };
}
